import axios from "axios";
import { tokenStore } from "./tokenStore";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const apiClient = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true, // send ASGARDEO_SESSION_ID cookie
  timeout: 15000,
});

// Attach the bearer token on every request. The HttpOnly session cookie
// (sent via withCredentials) is the primary credential; this is the fallback.
apiClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwrap the standardized API envelope and handle 401 refresh
apiClient.interceptors.response.use(
  (response) => {
    // Unwrap standard envelope: { status, code, data, pagination? }
    const body = response.data;
    if (
      body &&
      typeof body === "object" &&
      body.status === "success" &&
      "data" in body
    ) {
      // Attach pagination to response for components that need it
      response.data = body.data;
      if (body.pagination) {
        response.pagination = body.pagination;
      }
    }
    return response;
  },
  async (error) => {
    const original = error.config;

    // Extract error message from envelope if available
    const envelope = error.response?.data;
    if (
      envelope &&
      typeof envelope === "object" &&
      envelope.status === "error"
    ) {
      error.serverMessage = envelope.message || "Request failed";
      error.serverError = envelope.error || envelope.message;
    }

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.endsWith("/refresh-token")
    ) {
      original._retry = true;
      const refreshTokenStr = tokenStore.getRefresh();
      if (refreshTokenStr) {
        try {
          const { data } = await axios.post(
            `${API_BASE}/api/refresh-token`,
            { refreshToken: refreshTokenStr },
            { withCredentials: true }
          );
          // Handle both wrapped and raw responses
          const tokenData = data?.data ?? data;
          if (tokenData?.accessToken) {
            tokenStore.set(tokenData.accessToken, tokenData.refreshToken);
            original.headers.Authorization = `Bearer ${tokenData.accessToken}`;
            return apiClient(original);
          }
        } catch {
          // Refresh failed, fall through to clear and redirect
        }
      }
      tokenStore.clear();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const API_ROOT = API_BASE;

export const goToLogin = () => {
  window.location.href = `${API_BASE}/login`;
};

export const goToLogout = () => {
  tokenStore.clear();
  window.location.href = `${API_BASE}/logout`;
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(window.atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? Date.now() >= payload.exp * 1000 : false;
  } catch {
    return true;
  }
};
